"use client";

import { Input } from "@frontend/shared";
import React, { useState, useRef } from "react";

import { Button, type VariantProps, buttonVariants } from "@frontend/shared";
import { Calendar } from "@frontend/shared/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@frontend/shared/components/ui/popover";
import { cn } from "@frontend/shared/lib/utils";
import { format, isValid, parse } from "date-fns";
import { Calendar as CalendarIcon, Minus, Plus, Search } from "lucide-react";
import { Check, ChevronsUpDown } from "lucide-react";

import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@frontend/shared/components/ui/command";

import { InsuranceMode } from "@frontend/lib/schemas/clientSchema";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@frontend/shared/components/ui/tabs";
import type { Control, FieldValues, UseFormSetValue } from "react-hook-form";

export interface InputProps
  extends React.InputHTMLAttributes<HTMLInputElement> {
  // biome-ignore lint/suspicious/noExplicitAny: This should be a generic that accepts anything.
  setValue?: UseFormSetValue<any>;
  valueName?: string;
}

export const V2TextInput = ({
  className,
  placeholder,
  onError,
  disabled,
  onChange,
  defaultValue,
  value,
  maxLength,
  ...props
}: {
  className?: string;
  placeholder?: string | undefined;
  onError?: React.ReactEventHandler<HTMLInputElement> | undefined;
  disabled?: boolean | undefined;
  onChange?: React.ChangeEventHandler<HTMLInputElement> | undefined;
  defaultValue?: string | number | readonly string[] | null;
  value?: string | number | readonly string[] | null;
  maxLength?: number;
}) => {
  return (
    <Input
      onChange={onChange}
      defaultValue={defaultValue ?? ""}
      style={{ flex: "1 1 0" }}
      className={cn(
        "bg-[#EDF9FF] focus:placeholder:text-gray-300 placeholder:transition-colors hover:placeholder-black border-none rounded-full px-6 font-medium placeholder:text-gray-300 text-black-500 h-[48px] placeholder-shown:bg-[#FAFAFA]",
      )}
      placeholder={placeholder}
      onError={onError}
      disabled={disabled}
      value={value ?? undefined}
      maxLength={maxLength}
      {...props}
    />
  );
};

interface V2ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  noShadow?: boolean;
  useStroke?: boolean;
  icon?: React.ReactNode;
  iconPosition?: "left" | "right";
}

export const V2ButtonBlue = React.forwardRef<HTMLButtonElement, V2ButtonProps>(
  (
    {
      className,
      variant,
      size,
      noShadow,
      useStroke,
      asChild = false,
      icon,
      iconPosition = "left",
      ...props
    },
    ref,
  ) => {
    const classes = buttonVariants({ variant, size });
    const cnClasses = cn(
      classes,
      "flex gap-1 rounded-full px-8 bg-superscriptBlue hover:bg-superscriptBlue/80 text-white",
      useStroke ? "border-white border-2" : "",
      className,
    );
    return (
      <button
        ref={ref}
        style={{
          boxShadow: noShadow ? "" : godAwfulDropShadow,
          ...props.style,
        }}
        className={cnClasses}
        {...props}
      >
        {iconPosition === "left" && icon}
        {props.children}
        {iconPosition === "right" && icon}
      </button>
    );
  },
);

V2ButtonBlue.displayName = "V2ButtonBlue";

export const V2ButtonWhite = React.forwardRef<HTMLButtonElement, V2ButtonProps>(
  (
    {
      className,
      variant,
      size,
      noShadow,
      useStroke,
      asChild = false,
      icon,
      iconPosition = "left",
      ...props
    },
    ref,
  ) => {
    const classes = buttonVariants({ variant, size });
    const cnClasses = cn(
      classes,
      "flex gap-1 rounded-full px-8 bg-white hover:bg-gray-100/80 text-superscriptBlue",
      useStroke ? "border-superscriptBlue border-2" : "",
      className,
    );
    return (
      <button
        ref={ref}
        className={cnClasses}
        style={{
          boxShadow: noShadow ? "" : godAwfulDropShadow,
          ...props.style,
        }}
        {...props}
      >
        {iconPosition === "left" && icon}
        {props.children}
        {iconPosition === "right" && icon}
      </button>
    );
  },
);

V2ButtonWhite.displayName = "V2ButtonWhite";

export function formatDobInput(input: string) {
  if (!input) {
    return "";
  }
  if (input.charAt(input.length - 1) === "/") {
  }

  const numericInput = input.replace(/\D/g, "");

  if (numericInput.length < 3) {
    return numericInput;
  }
  if (numericInput.length < 5) {
    return `${numericInput.substring(0, 2)}/${numericInput.substring(2)}`;
  }

  return `${numericInput.substring(0, 2)}/${numericInput.substring(
    2,
    4,
  )}/${numericInput.substring(4, 8)}`;
}

export const V2DateInput = React.forwardRef<HTMLInputElement, InputProps>(
  ({ className, type, setValue, valueName, placeholder, ...props }, ref) => {
    const inputRef = useRef<HTMLInputElement>(null);

    const [selected, setSelected] = useState<Date | undefined>();
    const [input, setInput] = useState(
      selected && isValid(selected) ? format(selected, "MM/dd/yyyy") : "",
    );

    const handleChange = (date: string) => {
      const formattedInput = formatDobInput(date);
      setInput(formattedInput);
      const newDate = parse(formattedInput, "MM/dd/yyyy", new Date());
      if (isValid(newDate)) {
        setSelected(newDate);
        const ymd = format(newDate, "MM/dd/yyyy");
        if (setValue && valueName) setValue(valueName, ymd);
      } else {
        if (setValue && valueName) setValue(valueName, "");
      }
    };

    const handleDaySelect = (date: Date | undefined) => {
      setSelected(date);
      if (date) {
        //const formattedInput = formatDobInput(date.toDateString());
        setInput(format(date, "MM/dd/yyyy"));
        if (setValue && valueName)
          setValue(valueName, format(date, "MM/dd/yyyy"));
      } else {
        setInput("");
      }
    };

    // onChange is overwritten in shadcn forms; use the useInputFormatter custom hook.
    return (
      <div className="bg-[#FAFAFA] flex  rounded-full w-full justify-between relative">
        <Input
          data-cy="birthday-input"
          placeholder={"MM/DD/YYYY"}
          className="border-none focus:placeholder:text-gray-300 placeholder:text-sm placeholder:font-medium text-sm placeholder:transition-colors rounded-full bg-inherit pl-6 placeholder:text-gray-300 hover:placeholder:text-black w-full focus:border-black focus:border outline-none  h-[48px]"
          value={input}
          onChange={(e) => handleChange(e.target.value)}
        />
        <Popover>
          <PopoverTrigger asChild>
            <Button
              variant={"link"}
              className={cn(
                "w-12 justify-start text-left font-normal absolute right-1 top-1",
                !selected && "text-muted-foreground",
              )}
            >
              <CalendarIcon className="h-4 w-4 text-gray-400" strokeWidth={3} />
            </Button>
          </PopoverTrigger>
          <PopoverContent
            className="w-auto p-0 text-superscriptBlue rounded-2xl"
            align="end"
          >
            <Calendar
              mode="single"
              selected={selected || new Date()}
              onSelect={handleDaySelect}
              className="rounded-md"
              captionLayout="dropdown"
              fromYear={1900}
              toDate={new Date()}
              defaultMonth={selected || new Date()}
            />
          </PopoverContent>
        </Popover>
      </div>
    );
  },
);

export const V2Row = ({
  className,
  children,
}: {
  className?: string;
  children: React.ReactNode;
}) => {
  return (
    <div className={cn("flex flex-row gap-4 w-full items-center", className)}>
      {children}
    </div>
  );
};

export const V2Col = ({
  className,
  children,
}: {
  className?: string;
  children: React.ReactNode;
}) => {
  return (
    <div className={cn("flex flex-col gap-4 w-full", className)}>
      {children}
    </div>
  );
};

export const godAwfulDropShadow = "rgba(0,0,0,0.1) 0px 0px 8px 3px";

/**
 *
 * @info still need to include TabsList and TabContent
 */
export const V2InsuranceTabs = ({
  labelsOnly,
  disabled,
  val,
  setVal,
  useSecondary,
  setUseSecondary,
  control,
}: {
  labelsOnly?: boolean;
  disabled?: boolean;
  val: InsuranceMode;
  setVal: React.Dispatch<React.SetStateAction<InsuranceMode>>;
  useSecondary: boolean;
  setUseSecondary: React.Dispatch<React.SetStateAction<boolean>>;
  control?: Control<FieldValues>;
}) => {
  const getIndicatorString = (val: InsuranceMode) => {
    let indicatorString =
      "absolute bg-superscriptBlue w-[50%] h-full border-white border-8 transition-all rounded-full ";
    indicatorString +=
      val === InsuranceMode.INSURANCE
        ? "left-100 -translate-x-1/2 "
        : "right-100 translate-x-1/2";
    return indicatorString;
  };

  const options = [
    {
      value: "next.js",
      label: "Next.js",
    },
    {
      value: "sveltekit",
      label: "SvelteKit",
    },
    {
      value: "nuxt.js",
      label: "Nuxt.js",
    },
  ];

  const handleClickSecondary = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    setUseSecondary(!useSecondary);
  };

  const InsuranceEntry = (
    <>
      <V2Row>
        <V2ComboBox
          icon={<Search className="size-4" />}
          className="flex-1 justify-between"
          placeholder="Plan"
          options={options}
        />
        <V2TextInput className="flex-1" placeholder="Member ID" />
      </V2Row>

      <V2Row className="w-[50%]">
        <V2ComboBox
          className="w-full mr-36 justify-between"
          placeholder="State"
          options={options}
        />
      </V2Row>
    </>
  );

  return (
    <Tabs
      defaultValue="selfpay"
      className="w-full "
      onValueChange={(val) => {
        const enumVal =
          val === "INSURED" ? InsuranceMode.INSURANCE : InsuranceMode.SELFPAY;
        setVal(enumVal);
      }}
    >
      <TabsList
        className="w-full rounded-full bg-white relative p-3"
        style={{ boxShadow: godAwfulDropShadow }}
      >
        <TabsTrigger
          className="data-[state=active]:shadow-none data-[state=active]:text-white rounded-full z-10 py-2 data-[state=active]:bg-transparent bg-transparent font-light text-sm"
          disabled={disabled}
          value="insurance"
        >
          Pay with insurance
        </TabsTrigger>
        <TabsTrigger
          className="data-[state=active]:shadow-none rounded-full data-[state=active]:text-white z-10 py-2 data-[state=active]:bg-transparent bg-transparent  font-light text-sm"
          disabled={disabled}
          value="selfpay"
        >
          Pay out of pocket
        </TabsTrigger>
        <div
          style={{ transition: "all 0.3s ease" }}
          className={getIndicatorString(val)}
        />
      </TabsList>
      {!labelsOnly && (
        <>
          <TabsContent
            value="insurance"
            style={{ transition: "all ease 0.2s" }}
            className={cn("w-full")}
          >
            <div
              style={{ transition: "all ease 1s" }}
              className={cn(
                "flex justify-center items-center pt-6 w-full",
                val === InsuranceMode.INSURANCE ? "h-full" : "h-[0%]",
              )}
            >
              <V2Col>
                {InsuranceEntry}
                {useSecondary && (
                  <>
                    <V2Row className="items-center">
                      <div className="bg-gray-200 h-px flex-1" />
                      <p className="text-sm text-gray-300">
                        Secondary Insurance
                      </p>
                      <div className="bg-gray-200 h-px flex-1" />
                    </V2Row>
                    {InsuranceEntry}
                  </>
                )}
                <V2Row>
                  <V2ButtonWhite className="mt-10">Run Check</V2ButtonWhite>
                  <V2ButtonWhite
                    icon={
                      useSecondary ? (
                        <Minus className="size-4" />
                      ) : (
                        <Plus className="size-4" />
                      )
                    }
                    onClick={handleClickSecondary}
                    className="mt-10 text-gray-500"
                  >
                    {useSecondary ? "Remove Secondary" : "Add Secondary"}
                  </V2ButtonWhite>
                </V2Row>
              </V2Col>
            </div>
          </TabsContent>
          <TabsContent value="selfpay">
            <div className="flex justify-center items-center pt-6">
              <p className="text-light text-gray-500">
                {" "}
                Proceeding without insurance.
              </p>
            </div>
          </TabsContent>
        </>
      )}
    </Tabs>
  );
};

export const V2ComboBox = ({
  className,
  placeholder,
  options,
  icon,
}: {
  className?: string;
  placeholder: string;
  icon?: React.ReactNode;
  options: {
    value: string;
    label: string;
  }[];
}) => {
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState("");

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          aria-expanded={open}
          className={cn(
            "bg-[#FAFAFA] hover:bg-[#FAFAFA] border-none rounded-xl px-6 font-medium placeholder:text-gray-300",
            className,
            open ? "text-black-300" : "",
            value === "" ? "text-gray-300" : "",
          )}
        >
          <div className="flex flex-row gap-2 items-center">
            {icon}
            {value
              ? options.find((option) => option.value === value)?.label
              : placeholder}
          </div>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-full p-0 popover-content-width-same-as-its-trigger rounded-2xl"
        align="start"
      >
        <Command className="rounded-2xl">
          <CommandInput placeholder={placeholder} />
          <CommandList>
            <CommandEmpty>No option found.</CommandEmpty>
            <CommandGroup>
              {options.map((option) => (
                <CommandItem
                  key={option.value}
                  value={option.value}
                  onSelect={(currentValue) => {
                    setValue(currentValue === value ? "" : currentValue);
                    setOpen(false);
                  }}
                >
                  <Check
                    className={cn(
                      "mr-2 h-4 w-4",
                      value === option.value ? "opacity-100" : "opacity-0",
                    )}
                  />
                  {option.label}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};
